export type QueryParams = Record<string, string | string[] | undefined>;

/**
 * Constructs a safe URL from the given base URL or path and optional query parameters.
 *
 * @param {string | URL} baseAndPath - The base URL or full URL with path.
 * @param {QueryParams} [qps] - Optional query parameters to append to the URL.
 * @returns {string} - The constructed URL as a string.
 * @throws {Error} - If the provided `baseAndPath` is not a valid URL.
 */
export function buildSafeUrl(
  baseAndPath: string | URL,
  qps?: QueryParams,
): string {
  let url: URL;

  try {
    url = typeof baseAndPath === "string" ? new URL(baseAndPath) : baseAndPath;
  } catch {
    throw new Error(`Invalid URL: ${baseAndPath}`);
  }

  if (qps) {
    Object.entries(qps).forEach(([key, value]) => {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((ivalue) => {
            url.searchParams.append(key, ivalue);
          });
        } else {
          url.searchParams.append(key, value);
        }
      }
    });
  }

  return url.toString();
}

/**
 * Constructs a safe URL from a base URL, an optional string path, and optional query parameters.
 *
 * @param {string | URL} base - The base URL to use.
 * @param {string | undefined} [path] - An optional path to append to the base.
 * @param {QueryParams} [qps] - Optional query parameters to append to the URL.
 * @returns {string} - The constructed URL as a string.
 * @throws {Error} - If the provided `base` is not a valid URL or if the `path` is invalid.
 */
export function buildSafeUrlWithBase(
  base: string | URL,
  path?: string,
  qps?: QueryParams,
): string {
  let baseUrl: URL;

  try {
    baseUrl = typeof base === "string" ? new URL(base) : base;
  } catch {
    throw new Error(`Invalid base URL: ${base}`);
  }

  let fullUrl: URL;

  try {
    fullUrl = path ? new URL(path, baseUrl) : baseUrl; // `path` can only be a string or undefined
  } catch {
    throw new Error(
      `Invalid path or base combination: base='${base}', path='${path}'`,
    );
  }

  return buildSafeUrl(fullUrl, qps);
}

/**
 * Converts a URLSearchParams object into a `QueryParams` object,
 * where duplicate keys are represented as arrays.
 *
 * @param {URLSearchParams} sps - The URLSearchParams object to convert.
 * @returns {QueryParams} The resulting object with key-value pairs.
 *                        If a key occurs multiple times, its value is an array.
 *
 * @example
 * const params = new URLSearchParams('sku=1&sku=2&bid=123');
 * convertSearchParamsToQueryParams(params);
 * // Output: { sku: ['1', '2'], bid: '123' }
 */
export function convertSearchParamsToQueryParams(
  sps: URLSearchParams,
): QueryParams {
  const result: QueryParams = {};
  sps.forEach((value, key) => {
    if (result[key]) {
      // If the key already exists, convert it to an array (if it isn't already) and add the value
      result[key] = Array.isArray(result[key])
        ? [...result[key], value]
        : [result[key], value];
    } else {
      // Otherwise, just assign the value
      result[key] = value;
    }
  });
  return result;
}

/**
 * Converts a query string into a `QueryParams` object,
 * where duplicate keys are represented as arrays.
 *
 * @param {string} qpstr - The query string to convert (e.g., '?sku=1&sku=2&bid=123').
 * @returns {QueryParams} The resulting object with key-value pairs.
 *                        If a key occurs multiple times, its value is an array.
 *
 * @example
 * convertStringQueryParamsToQueryParams('?sku=1&sku=2&bid=123');
 * // Output: { sku: ['1', '2'], bid: '123' }
 */
export function convertStringQueryParamsToQueryParams(
  qpstr: string,
): QueryParams {
  return convertSearchParamsToQueryParams(new URLSearchParams(qpstr));
}
